// ============================================================
// ELC Shift — Uploads / Excel export module (v101.0)
//
// Multi-sheet xlsx export for the admin export panel. Builds
// per-store sheets + an aggregate (all-stores summary) sheet,
// then triggers download via XLSX (SheetJS).
//
// Two user-facing exports:
//   • downloadStoreExport({store, from, to})         — single store
//   • downloadAllStoresExport({from, to})            — all stores +
//                                                       aggregate sheet
//
// Helpers (also exported for testability + reuse):
//   • fetchExportData(from, to)        — DB facade
//   • enumerateDates(from, to)         — date-range expansion
//   • buildStoreSheetRows(store, ...)  — single store aoa rows
//   • buildAggregateSheetRows(...)     — country/total aoa rows
//
// vs-LY column uses a -364 day offset to match same-day-of-week
// from prior year (banked v79.12 invariant — pinned by
// kpi-screen-shape_test).
//
// Free globals consumed:
//   • DB (admin_exports.fetch)         — data source
//   • XLSX                             — SheetJS global
//   • showToast                        — UI feedback
//   • parseNum, parseInt etc.          — number helpers
//   • formatDateShort, daysAgo etc.    — date helpers in admin.html
// ============================================================

import { ensureXlsx } from '../shared/ensure-xlsx.js';   // v136.8: the spreadsheet library loads only when it is used

export async function fetchExportData(from, to) {
  return DB.admin_exports.fetch({ from, to });
}

export function enumerateDates(fromStr, toStr) {
  const out = [];
  let [y,m,d] = fromStr.split('-').map(Number);
  const end = new Date(toStr + 'T00:00:00Z').getTime();
  let cur = new Date(Date.UTC(y, m-1, d));
  while (cur.getTime() <= end) {
    out.push(cur.getUTCFullYear() + '-' + String(cur.getUTCMonth()+1).padStart(2,'0') + '-' + String(cur.getUTCDate()).padStart(2,'0'));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return out;
}

export function buildStoreSheetRows(storeName, data, dateList) {
  const dayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  // v79.12: targets columns for each addon now sit next to the actuals so
  // the reader can see actual / target side-by-side. The targets reflect
  // any active run-rate scaling so the % achievements match what staff
  // see on the KPI screen.
  const headers = [
    'Day','Date','Sales','Target','vs LY %',
    'Demo Actual','Demo Target','Bat Actual','Bat Target',
    'Wrap Actual','Wrap Target','Bag Actual','Bag Target',
    'Cards Actual','Cards Target',
    'Run Rate %','Note','Demo Line',
  ];
  const rows = [headers];

  // Index data for this store by date
  const salesByDate    = {}, targetsByDate = {}, salesLyByDate = {}, addonsByDate = {}, notesByDate = {}, demoByWeekScope = {};
  (data.sales         || []).filter(r => r.store === storeName).forEach(r => salesByDate[r.date]   = Number(r.sales));
  (data.targets       || []).filter(r => r.store === storeName).forEach(r => targetsByDate[r.date] = Number(r.value));
  (data.addon_actuals || []).filter(r => r.store === storeName).forEach(r => addonsByDate[r.date]  = r);
  (data.daily_notes   || []).filter(r => r.store === storeName).forEach(r => notesByDate[r.date]   = r.note);

  // v79.12: demo line hierarchy lookup. Pre-group store/country/company
  // entries by week so the per-day target computation can pick the most
  // specific match — same rule as the worker's /api/kpi handler.
  const storeMeta = (data.stores || []).find(s => s.name === storeName);
  const country = storeMeta ? storeMeta.country : null;
  (data.demo_lines || []).forEach(r => {
    const wk = r.iso_year + '-' + String(r.iso_week).padStart(2,'0');
    // Match if scope=store and store=storeName, OR scope=country and store=country, OR scope=company and store='COMPANY'.
    const scope = r.scope || 'store';
    let matches = false;
    if (scope === 'store'   && r.store === storeName) matches = true;
    if (scope === 'country' && country && r.store === country) matches = true;
    if (scope === 'company' && r.store === 'COMPANY') matches = true;
    if (!matches) return;
    if (!demoByWeekScope[wk]) demoByWeekScope[wk] = {};
    demoByWeekScope[wk][scope] = r;
  });
  function demoLineFor(wk) {
    const d = demoByWeekScope[wk] || {};
    return d.store || d.country || d.company || null;
  }

  // v79.12: per-store run-rate lookup. Filter rows for this store, sort
  // by effective_from descending, pick first match by date.
  const myRunRates = (data.run_rates || [])
    .filter(r => r.store === storeName)
    .sort((a,b) => (b.effective_from || '').localeCompare(a.effective_from || ''));
  function runRateFor(date) {
    for (const r of myRunRates) {
      if (r.effective_from && date < r.effective_from) continue;
      if (r.effective_to   && date > r.effective_to)   continue;
      return Number(r.rate_pct) / 100;
    }
    return 1.0;
  }

  // v79.12: addon target formula — must match worker.js exactly.
  // CEIL for demo/bat/wrap/bag; CEIL on Saturday + ROUND otherwise for cards.
  const rates = data.addon_rates;
  function computeAddonTargets(target, dateStr, demoPrice, runRate) {
    if (!rates || !target || target <= 0) return null;
    const scaled = target * (runRate != null ? runRate : 1);
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    const isSat = dt.getUTCDay() === 6;
    return {
      demo:  demoPrice && demoPrice > 0 ? Math.ceil(scaled * Number(rates.demo_target_pct) / Number(demoPrice)) : null,
      bat:   Math.ceil(scaled * Number(rates.batteries_pct)    / Number(rates.batteries_price)),
      wrap:  Math.ceil(scaled * Number(rates.gift_wrap_pct)    / Number(rates.gift_wrap_price)),
      bag:   Math.ceil(scaled * Number(rates.reusable_bag_pct) / Number(rates.reusable_bag_price)),
      cards: isSat ? Math.ceil(scaled * Number(rates.gift_cards_pct) / Number(rates.gift_cards_price))
                   : Math.round(scaled * Number(rates.gift_cards_pct) / Number(rates.gift_cards_price)),
    };
  }

  // Helper: ISO week for a date string (matches the worker / staff app algorithm)
  function isoWeekKey(dateStr) {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    const dow = ((dt.getUTCDay() + 6) % 7) + 1;
    const thurs = new Date(dt);
    thurs.setUTCDate(dt.getUTCDate() + 4 - dow);
    const isoYear = thurs.getUTCFullYear();
    const jan4 = new Date(Date.UTC(isoYear, 0, 4));
    const jan4Dow = ((jan4.getUTCDay() + 6) % 7) + 1;
    const week1Mon = new Date(jan4);
    week1Mon.setUTCDate(jan4.getUTCDate() - jan4Dow + 1);
    const week = Math.floor((thurs - week1Mon) / (7 * 86400000)) + 1;
    return isoYear + '-' + (week < 10 ? '0' + week : '' + week);
  }

  dateList.forEach(date => {
    const [y, m, d] = date.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    const dayName = dayNames[dt.getUTCDay()];
    const sales = salesByDate[date] != null ? salesByDate[date] : null;
    const target = targetsByDate[date] != null ? targetsByDate[date] : null;
    // vs LY: lookup sales for date - 364 days
    const lyDate = new Date(Date.UTC(y, m - 1, d - 364));
    const lyKey = lyDate.getUTCFullYear() + '-' + String(lyDate.getUTCMonth()+1).padStart(2,'0') + '-' + String(lyDate.getUTCDate()).padStart(2,'0');
    const lySales = salesByDate[lyKey];
    const vsLy = (sales != null && lySales && lySales > 0) ? (sales - lySales) / lySales : null;
    const a = addonsByDate[date] || {};
    const note = notesByDate[date] || '';
    const dl = demoLineFor(isoWeekKey(date));
    const demoLineLabel = dl ? (dl.item_name + ' @ ' + Number(dl.retail_price).toFixed(2)) : '';
    const runMult = runRateFor(date);
    const t = computeAddonTargets(target, date, dl ? Number(dl.retail_price) : null, runMult);
    rows.push([
      dayName,
      date,
      sales,
      target,
      vsLy,
      a.demo_units    != null ? Number(a.demo_units)    : null,
      t ? t.demo : null,
      a.batteries     != null ? Number(a.batteries)     : null,
      t ? t.bat  : null,
      a.gift_wrap     != null ? Number(a.gift_wrap)     : null,
      t ? t.wrap : null,
      a.reusable_bag  != null ? Number(a.reusable_bag)  : null,
      t ? t.bag  : null,
      a.gift_cards    != null ? Number(a.gift_cards)    : null,
      t ? t.cards: null,
      runMult !== 1.0 ? Math.round(runMult * 100) : null,   // null = "normal" / not scaled
      note,
      demoLineLabel,
    ]);
  });
  return rows;
}

export function buildAggregateSheetRows(label, storeFilter, data, dateList) {
  const dayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  // v79.12: addon target columns added so the aggregate sheet matches
  // the per-store sheet's level of detail. Targets are run-rate-scaled
  // per store per date BEFORE summing — matches the volume-weighted
  // company-avg approach used elsewhere.
  const headers = [
    'Day','Date',label + ' Sales',label + ' Target','vs LY %',
    'Demo Actual','Demo Target','Bat Actual','Bat Target',
    'Wrap Actual','Wrap Target','Bag Actual','Bag Target',
    'Cards Actual','Cards Target',
    'Stores reporting',
  ];
  const rows = [headers];

  // Index aggregates
  const salesByDate = {}, targetsByDate = {}, addonsSum = {}, storesByDate = {};
  // v79.12: pre-scaled addon targets summed per date.
  const addonTargetSum = {};
  function add(date, key, val) {
    if (val == null) return;
    if (!addonsSum[date]) addonsSum[date] = {};
    addonsSum[date][key] = (addonsSum[date][key] || 0) + Number(val);
  }
  function addTarget(date, key, val) {
    if (val == null) return;
    if (!addonTargetSum[date]) addonTargetSum[date] = {};
    addonTargetSum[date][key] = (addonTargetSum[date][key] || 0) + Number(val);
  }
  (data.sales || []).forEach(r => {
    if (!storeFilter(r.store)) return;
    salesByDate[r.date] = (salesByDate[r.date] || 0) + Number(r.sales);
    if (!storesByDate[r.date]) storesByDate[r.date] = new Set();
    storesByDate[r.date].add(r.store);
  });
  (data.targets || []).forEach(r => {
    if (!storeFilter(r.store)) return;
    targetsByDate[r.date] = (targetsByDate[r.date] || 0) + Number(r.value);
  });
  (data.addon_actuals || []).forEach(r => {
    if (!storeFilter(r.store)) return;
    add(r.date, 'demo_units',   r.demo_units);
    add(r.date, 'batteries',    r.batteries);
    add(r.date, 'gift_wrap',    r.gift_wrap);
    add(r.date, 'reusable_bag', r.reusable_bag);
    add(r.date, 'gift_cards',   r.gift_cards);
  });

  // v79.12: build per-store run-rate + demo-line lookups so we can
  // compute scaled addon targets per store-date, then sum.
  const rates = data.addon_rates;
  const runRatesByStore = {};
  (data.run_rates || []).forEach(r => {
    if (!runRatesByStore[r.store]) runRatesByStore[r.store] = [];
    runRatesByStore[r.store].push(r);
  });
  Object.values(runRatesByStore).forEach(arr =>
    arr.sort((a,b) => (b.effective_from || '').localeCompare(a.effective_from || ''))
  );
  function runRateMul(storeName, date) {
    const list = runRatesByStore[storeName];
    if (!list) return 1.0;
    for (const r of list) {
      if (r.effective_from && date < r.effective_from) continue;
      if (r.effective_to   && date > r.effective_to)   continue;
      return Number(r.rate_pct) / 100;
    }
    return 1.0;
  }
  // Demo line by (scope, target, week).
  const demoByKey = {};
  (data.demo_lines || []).forEach(r => {
    const wk = r.iso_year + '-' + String(r.iso_week).padStart(2,'0');
    demoByKey[(r.scope || 'store') + '::' + r.store + '::' + wk] = r;
  });
  const countryByStore = {};
  (data.stores || []).forEach(s => { countryByStore[s.name] = s.country; });
  function demoLineFor(storeName, date) {
    const wk = isoWeekKey(date);
    const country = countryByStore[storeName];
    return demoByKey['store::' + storeName + '::' + wk]
        || (country && demoByKey['country::' + country + '::' + wk])
        || demoByKey['company::COMPANY::' + wk]
        || null;
  }
  function isoWeekKey(dateStr) {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    const dow = ((dt.getUTCDay() + 6) % 7) + 1;
    const thurs = new Date(dt);
    thurs.setUTCDate(dt.getUTCDate() + 4 - dow);
    const isoYear = thurs.getUTCFullYear();
    const jan4 = new Date(Date.UTC(isoYear, 0, 4));
    const jan4Dow = ((jan4.getUTCDay() + 6) % 7) + 1;
    const week1Mon = new Date(jan4);
    week1Mon.setUTCDate(jan4.getUTCDate() - jan4Dow + 1);
    const week = Math.floor((thurs - week1Mon) / (7 * 86400000)) + 1;
    return isoYear + '-' + (week < 10 ? '0' + week : '' + week);
  }

  // Walk per-store target rows, compute per-day scaled addon targets, sum into addonTargetSum.
  (data.targets || []).forEach(r => {
    if (!storeFilter(r.store)) return;
    if (!rates || r.value == null || Number(r.value) <= 0) return;
    const dl = demoLineFor(r.store, r.date);
    const demoPrice = dl ? Number(dl.retail_price) : null;
    const runMult = runRateMul(r.store, r.date);
    const scaled = Number(r.value) * runMult;
    const [y, m, d] = r.date.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    const isSat = dt.getUTCDay() === 6;
    if (demoPrice && demoPrice > 0) {
      addTarget(r.date, 'demo', Math.ceil(scaled * Number(rates.demo_target_pct) / demoPrice));
    }
    addTarget(r.date, 'bat',   Math.ceil(scaled * Number(rates.batteries_pct)    / Number(rates.batteries_price)));
    addTarget(r.date, 'wrap',  Math.ceil(scaled * Number(rates.gift_wrap_pct)    / Number(rates.gift_wrap_price)));
    addTarget(r.date, 'bag',   Math.ceil(scaled * Number(rates.reusable_bag_pct) / Number(rates.reusable_bag_price)));
    addTarget(r.date, 'cards', isSat
      ? Math.ceil(scaled * Number(rates.gift_cards_pct) / Number(rates.gift_cards_price))
      : Math.round(scaled * Number(rates.gift_cards_pct) / Number(rates.gift_cards_price)));
  });

  dateList.forEach(date => {
    const [y, m, d] = date.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    const dayName = dayNames[dt.getUTCDay()];
    const sales = salesByDate[date] != null ? salesByDate[date] : null;
    const target = targetsByDate[date] != null ? targetsByDate[date] : null;
    const lyDate = new Date(Date.UTC(y, m - 1, d - 364));
    const lyKey = lyDate.getUTCFullYear() + '-' + String(lyDate.getUTCMonth()+1).padStart(2,'0') + '-' + String(lyDate.getUTCDate()).padStart(2,'0');
    const lySales = salesByDate[lyKey];
    const vsLy = (sales != null && lySales && lySales > 0) ? (sales - lySales) / lySales : null;
    const a = addonsSum[date] || {};
    const t = addonTargetSum[date] || {};
    rows.push([
      dayName, date, sales, target, vsLy,
      a.demo_units   != null ? a.demo_units   : null,
      t.demo  != null ? t.demo  : null,
      a.batteries    != null ? a.batteries    : null,
      t.bat   != null ? t.bat   : null,
      a.gift_wrap    != null ? a.gift_wrap    : null,
      t.wrap  != null ? t.wrap  : null,
      a.reusable_bag != null ? a.reusable_bag : null,
      t.bag   != null ? t.bag   : null,
      a.gift_cards   != null ? a.gift_cards   : null,
      t.cards != null ? t.cards : null,
      storesByDate[date] ? storesByDate[date].size : 0,
    ]);
  });
  return rows;
}

export async function downloadStoreExport() {
  const sel = document.getElementById('exportStoreSelect');
  const from = document.getElementById('exportFromDate').value;
  const to   = document.getElementById('exportToDate').value;
  if (!sel || !sel.value) { showToast('Pick a store first', 'error'); return; }
  if (!from || !to)        { showToast('Set from/to dates', 'error'); return; }
  const store = sel.value;
  const status = document.getElementById('exportStatus');
  if (status) status.textContent = 'Loading data…';
  try {
    await ensureXlsx();   // v136.8: loads on first use; a failed load lands in the catch below with a plain message
    const data = await fetchExportData(from, to);
    const dateList = enumerateDates(from, to);
    const rows = buildStoreSheetRows(store, data, dateList);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{wch:9},{wch:12},{wch:11},{wch:11},{wch:9},{wch:8},{wch:8},{wch:8},{wch:8},{wch:8},{wch:40},{wch:30}];
    ws['!freeze'] = { ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, ws, store.slice(0, 30));   // sheet name 31 char limit
    const safeName = store.replace(/[^a-z0-9]+/gi, '_');
    XLSX.writeFile(wb, 'ELC_' + safeName + '_' + from + '_to_' + to + '.xlsx');
    if (status) status.textContent = 'Downloaded.';
    showToast('Store export downloaded', 'success');
  } catch (e) {
    console.warn('[export] store export failed:', e);
    if (status) status.textContent = 'Failed: ' + e.message;
    showToast('Export failed: ' + e.message, 'error');
  }
}

export async function downloadAllStoresExport() {
  const from = document.getElementById('exportFromDate').value;
  const to   = document.getElementById('exportToDate').value;
  if (!from || !to) { showToast('Set from/to dates', 'error'); return; }
  const status = document.getElementById('exportStatus');
  if (status) status.textContent = 'Loading data…';
  try {
    await ensureXlsx();   // v136.8: loads on first use; a failed load lands in the catch below with a plain message
    const data = await fetchExportData(from, to);
    const dateList = enumerateDates(from, to);

    // Group stores by country using the stores+areas info in `data`
    const storesByName = {};
    const countryByStore = {};
    (data.stores || []).forEach(s => {
      storesByName[s.name] = s;
      countryByStore[s.name] = s.country || 'Unknown';
    });
    const allStoreNames = Object.keys(storesByName).sort();

    const wb = XLSX.utils.book_new();

    // 1. Per-store tabs
    if (status) status.textContent = 'Building per-store tabs…';
    allStoreNames.forEach(store => {
      const rows = buildStoreSheetRows(store, data, dateList);
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [{wch:9},{wch:12},{wch:11},{wch:11},{wch:9},{wch:8},{wch:8},{wch:8},{wch:8},{wch:8},{wch:40},{wch:30}];
      ws['!freeze'] = { ySplit: 1 };
      // Sheet names: 31 char limit, no special chars (Excel rejects /\?*[]:)
      const safeSheet = store.replace(/[\/\\\?\*\[\]:]/g, ' ').slice(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, safeSheet);
    });

    // 2. Country tabs
    const countries = Array.from(new Set(Object.values(countryByStore))).filter(c => c && c !== 'Unknown').sort();
    countries.forEach(country => {
      const rows = buildAggregateSheetRows(country, s => countryByStore[s] === country, data, dateList);
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [{wch:9},{wch:12},{wch:14},{wch:14},{wch:9},{wch:10},{wch:10},{wch:10},{wch:10},{wch:10},{wch:14}];
      ws['!freeze'] = { ySplit: 1 };
      const safeSheet = ('🌍 ' + country).replace(/[\/\\\?\*\[\]:]/g, ' ').slice(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, safeSheet);
    });

    // 3. Company total tab (always last, named "🏢 Total")
    const totalRows = buildAggregateSheetRows('Company', () => true, data, dateList);
    const totalWs = XLSX.utils.aoa_to_sheet(totalRows);
    totalWs['!cols'] = [{wch:9},{wch:12},{wch:14},{wch:14},{wch:9},{wch:10},{wch:10},{wch:10},{wch:10},{wch:10},{wch:14}];
    totalWs['!freeze'] = { ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, totalWs, '🏢 Total');

    XLSX.writeFile(wb, 'ELC_All_Stores_' + from + '_to_' + to + '.xlsx');
    if (status) status.textContent = 'Downloaded — ' + allStoreNames.length + ' stores + ' + countries.length + ' countries + total.';
    showToast('All-stores export downloaded', 'success');
  } catch (e) {
    console.warn('[export] all-stores export failed:', e);
    if (status) status.textContent = 'Failed: ' + e.message;
    showToast('Export failed: ' + e.message, 'error');
  }
}

